import { useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FiCheck, FiChevronLeft, FiChevronRight, FiCopy, FiDownload, FiFilter, FiSearch } from 'react-icons/fi';
import { GET_USER_TRANSACTIONS } from '../../graphql/queries/transactions';
import { Loader } from '../../shared/Loader';
import {
  GetUserTransactionsData,
  PaginationInput,
  TransactionFilterInput,
  TransactionStatus,
  TransactionType
} from '../../types/transactions';
import { exportToCSV } from '../../utils/csvExport';
import { useNavigate } from 'react-router-dom';

export const TransactionHistory = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Applied filter states (used for GraphQL query)
  const [appliedTypes, setAppliedTypes] = useState<TransactionType[]>([]);
  const [appliedStatuses, setAppliedStatuses] = useState<TransactionStatus[]>([]);
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [appliedMinAmount, setAppliedMinAmount] = useState<number | ''>('');
  const [appliedMaxAmount, setAppliedMaxAmount] = useState<number | ''>('');

  // Pending filter states (used for UI form)
  const [selectedTypes, setSelectedTypes] = useState<TransactionType[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<TransactionStatus[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minAmount, setMinAmount] = useState<number | ''>('');
  const [maxAmount, setMaxAmount] = useState<number | ''>('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Build filter object using applied filters
  const filters: TransactionFilterInput = useMemo(() => {
    const filterObj: TransactionFilterInput = {};

    if (appliedTypes.length > 0) filterObj.types = appliedTypes;
    if (appliedStatuses.length > 0) filterObj.statuses = appliedStatuses;
    if (appliedStartDate) filterObj.startDate = appliedStartDate;
    if (appliedEndDate) filterObj.endDate = appliedEndDate;
    if (appliedMinAmount !== '') filterObj.minAmount = Number(appliedMinAmount);
    if (appliedMaxAmount !== '') filterObj.maxAmount = Number(appliedMaxAmount);
    if (searchTerm) filterObj.search = searchTerm;

    return filterObj;
  }, [appliedTypes, appliedStatuses, appliedStartDate, appliedEndDate, appliedMinAmount, appliedMaxAmount, searchTerm]);

  // Pagination object
  const pagination: PaginationInput = {
    page: currentPage,
    limit: pageSize
  };

  // GraphQL query
  const { data, loading, error, refetch } = useQuery<GetUserTransactionsData>(
    GET_USER_TRANSACTIONS,
    {
      variables: {
        filters,
        pagination
      },
      errorPolicy: 'all'
    }
  );



  // Handle type filter change
  const handleTypeChange = (type: TransactionType) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Handle status filter change
  const handleStatusChange = (status: TransactionStatus) => {
    setSelectedStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  // Apply pending filters
  const applyFilters = () => {
    setAppliedTypes(selectedTypes);
    setAppliedStatuses(selectedStatuses);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedMinAmount(minAmount);
    setAppliedMaxAmount(maxAmount);
    setCurrentPage(1); // Reset to first page when applying filters
  };

  // Clear all filters
  const clearFilters = () => {
    // Clear pending filters
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setStartDate('');
    setEndDate('');
    setMinAmount('');
    setMaxAmount('');
    setSearchTerm('');

    // Clear applied filters
    setAppliedTypes([]);
    setAppliedStatuses([]);
    setAppliedStartDate('');
    setAppliedEndDate('');
    setAppliedMinAmount('');
    setAppliedMaxAmount('');
    setCurrentPage(1);
  };

  // Check if there are pending changes
  const hasPendingChanges = useMemo(() => {
    return (
      JSON.stringify(selectedTypes) !== JSON.stringify(appliedTypes) ||
      JSON.stringify(selectedStatuses) !== JSON.stringify(appliedStatuses) ||
      startDate !== appliedStartDate ||
      endDate !== appliedEndDate ||
      minAmount !== appliedMinAmount ||
      maxAmount !== appliedMaxAmount
    );
  }, [selectedTypes, appliedTypes, selectedStatuses, appliedStatuses, startDate, appliedStartDate, endDate, appliedEndDate, minAmount, appliedMinAmount, maxAmount, appliedMaxAmount]);

  // Export to CSV
  const handleExport = () => {
    if (data?.getUserTransactions.data && data.getUserTransactions.data.length > 0) {
      exportToCSV(data.getUserTransactions.data, 'transaction-history');
    }
  };

  // Copy transaction ID to clipboard
  const copyTransactionId = async (transactionId: string) => {
    try {
      await navigator.clipboard.writeText(transactionId);
      setCopiedId(transactionId);
      toast.success('Transaction ID copied to clipboard');

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy transaction ID');
    }
  };

  // Status badge component. `refunded` (redbillerStatus === 'refunded') wins
  // over the raw status: the withdraw is CANCELLED underneath, but the user
  // needs to see that the money came back.
  const StatusBadge = ({ status, refunded }: { status: TransactionStatus; refunded?: boolean }) => {
    const getStatusClass = () => {
      if (refunded) return 'badge-info';
      switch (status) {
        case TransactionStatus.COMPLETED:
        case TransactionStatus.CONFIRMED:
          return 'badge-success';
        case TransactionStatus.PENDING:
          return 'badge-warning';
        case TransactionStatus.SUSPENDED:
          return 'badge-suspended';
        case TransactionStatus.PROCESSING:
        case TransactionStatus.PROCESSED:
        case TransactionStatus.UNDER_REVIEW:
          return 'badge-info';
        case TransactionStatus.CANCELLED:
        case TransactionStatus.REJECTED:
        case TransactionStatus.EXPIRED:
        case TransactionStatus.TIMEOUT:
        case TransactionStatus.REVERTED:
          return 'badge-error';
        default:
          return 'badge-info';
      }
    };

    return (
      <div className={`${getStatusClass()} px-3 py-1 rounded-full text-xs font-medium`}>
        {refunded ? 'REFUNDED' : status.replace('_', ' ')}
      </div>
    );
  };

  // Transaction type badge component
  const TypeBadge = ({ type }: { type: TransactionType }) => {
    const getTypeClass = () => {
      switch (type) {
        case TransactionType.WITHDRAW:
          return 'bg-red-500/20 text-red-400 border border-red-500/30';
        case TransactionType.GIFTCARD_SELL:
          return 'bg-green-500/20 text-green-400 border border-green-500/30';
        case TransactionType.CRYPTO_SELL:
          return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
        case TransactionType.WALLET_CRYPTO_SELL:
          return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
        case TransactionType.QUICK_CRYPTO_SELL:
          return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
        case TransactionType.CRYPTO_PAYOUT:
          return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
        case TransactionType.BANK_DEPOSIT:
          return 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30';
        case TransactionType.LEADERSHIP_BONUS:
          return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        case TransactionType.STREAK_BONUS:
          return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        case TransactionType.MOMO_WITHDRAW:
          return 'bg-teal-500/20 text-teal-400 border border-teal-500/30';
        default:
          return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
      }
    };

    const getTypeLabel = () => {
      switch (type) {
        case TransactionType.WITHDRAW:
          return 'Withdraw';
        case TransactionType.GIFTCARD_SELL:
          return 'Giftcard Sell';
        case TransactionType.CRYPTO_SELL:
          return 'Crypto Sell';
        case TransactionType.WALLET_CRYPTO_SELL:
          return 'Wallet Crypto';
        case TransactionType.QUICK_CRYPTO_SELL:
          return 'Quick Crypto';
        case TransactionType.CRYPTO_PAYOUT:
          return 'Crypto Payout';
        case TransactionType.BANK_DEPOSIT:
          return 'Bank Deposit';
        case TransactionType.LEADERSHIP_BONUS:
          return 'Leadership Bonus';
        case TransactionType.STREAK_BONUS:
          return 'Streak Reward';
        case TransactionType.MOMO_WITHDRAW:
          return 'Mobile Money';
        default:
          return String(type).replace('_', ' ');
      }
    };

    return (
      <div className={`${getTypeClass()} px-3 py-1 rounded-full text-xs font-medium w-full text-center`}>
        {type === TransactionType.STREAK_BONUS && (
          <span aria-hidden="true" className="mr-1">🔥</span>
        )}
        {getTypeLabel()}
      </div>
    );
  };



  if (error) {
    return (
      <div className="font-dashboard pt-4 pb-12 md:px-4 min-h-screen">
        <div className="container-lg">
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 text-center">
            <p className="text-red-500">Error loading transactions: {error.message}</p>
            <button
              onClick={() => refetch()}
              className="mt-2 btn-primary px-4 py-2 text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-dashboard pt-4 pb-12 md:px-4 min-h-screen">
      <div className="container-lg">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-white mb-4 md:mb-0">
            Transaction Report
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm transition-colors ${appliedTypes.length > 0 || appliedStatuses.length > 0 || appliedStartDate || appliedEndDate || appliedMinAmount !== '' || appliedMaxAmount !== ''
                ? 'bg-primary/20 border border-primary text-primary hover:bg-primary/30'
                : 'bg-gray-600 hover:bg-gray-500'
                }`}
            >
              <FiFilter className="text-base" />
              Filters
              {(appliedTypes.length > 0 || appliedStatuses.length > 0 || appliedStartDate || appliedEndDate || appliedMinAmount !== '' || appliedMaxAmount !== '') && (
                <span className="ml-1 bg-primary text-black text-xs px-2 py-0.5 rounded-full font-medium">
                  Active
                </span>
              )}
            </button>
            <button
              onClick={handleExport}
              disabled={!data?.getUserTransactions.data || data.getUserTransactions.data.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/80 disabled:bg-gray-500 disabled:cursor-not-allowed rounded-lg text-black text-sm transition-colors"
            >
              <FiDownload className="text-base" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search transactions by ID, type, status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary"
          />
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Transaction Types */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Transaction Types
                </label>
                <div className="space-y-2">
                  {Object.values(TransactionType).map(type => (
                    <label key={type} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(type)}
                        onChange={() => handleTypeChange(type)}
                        className="form-checkbox h-4 w-4 text-primary"
                      />
                      <span className="ml-2 text-sm text-gray-300 capitalize">
                        {type.replace('_', ' ').toLowerCase()}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Status
                </label>
                <div className="grid grid-cols-2 gap-2 overflow-y-auto">
                  {Object.values(TransactionStatus).map(status => (
                    <label key={status} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedStatuses.includes(status)}
                        onChange={() => handleStatusChange(status)}
                        className="form-checkbox h-4 w-4 text-primary"
                      />
                      <span className="ml-2 text-sm text-gray-300 capitalize">
                        {status.replace('_', ' ').toLowerCase()}
                      </span>
                    </label>
                  ))}
                </div>
              </div>



              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Date Range
                </label>
                <div className="space-y-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="Start Date"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="End Date"
                  />
                </div>
              </div>

              {/* Amount Range */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Amount Range
                </label>
                <div className="space-y-2">
                  <input
                    type="number"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="Min Amount"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <input
                    type="number"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="Max Amount"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                </div>
              </div>

              {/* Page Size */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Items per Page
                </label>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={applyFilters}
                  disabled={!hasPendingChanges}
                  className="px-6 py-2 bg-primary hover:bg-primary/80 disabled:bg-gray-500 disabled:cursor-not-allowed rounded-lg text-black text-sm font-medium transition-colors"
                >
                  Apply Filters
                </button>
                {hasPendingChanges && (
                  <span className="text-yellow-400 text-xs">
                    • You have unsaved filter changes
                  </span>
                )}
              </div>
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white text-sm transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-black rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          ) : !data?.getUserTransactions.data || data.getUserTransactions.data.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No transactions found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-400">
                <thead className="text-xs uppercase bg-gray-700 text-gray-300">
                  <tr>
                    <th scope="col" className="py-3 px-6">Transaction ID</th>
                    <th scope="col" className="py-3 px-6">Type</th>
                    <th scope="col" className="py-3 px-6">Amount</th>
                    <th scope="col" className="py-3 px-6">Coin</th>
                    <th scope="col" className="py-3 px-6">Status</th>
                    <th scope="col" className="py-3 px-6">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.getUserTransactions.data.map((transaction) => {
                    const isPayout = transaction.type === TransactionType.CRYPTO_PAYOUT;
                    return (
                    <tr
                      key={transaction._id}
                      onClick={isPayout ? () => navigate(`/app/crypto-payout/${transaction._id}`) : undefined}
                      className={`border-b bg-transparent border-gray-700 hover:bg-gray-800/50 ${isPayout ? "cursor-pointer" : ""}`}
                    >
                      <td className="py-4 px-6 font-medium text-white">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">
                            {transaction._id.slice(0, 8)}...{transaction._id.slice(-8)}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyTransactionId(transaction._id);
                            }}
                            className="p-1 rounded hover:bg-gray-700 transition-colors group"
                            title="Copy transaction ID"
                          >
                            {copiedId === transaction._id ? (
                              <FiCheck className="text-green-400 text-xs" />
                            ) : (
                              <FiCopy className="text-gray-400 group-hover:text-white text-xs" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <TypeBadge type={transaction.type} />
                        {(transaction.source === 'reward' || transaction.source === 'referral') && (
                          <span className='mt-1 block text-center text-[10px] uppercase tracking-wide text-white/50'>
                            from {transaction.source}
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-white font-semibold">
                          ₦{transaction.amountInNGN ? transaction.amountInNGN.toLocaleString() : transaction.amount.toLocaleString()}
                        </div>
                        {transaction.amountInUsd && (
                          <div className="text-gray-400 text-xs">
                            ${transaction.amountInUsd.toLocaleString()}
                          </div>
                        )}
                        {transaction.reason && (
                          <div className="text-gray-400 text-xs">
                            {transaction.reason}
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        {transaction.coin ? (
                          <span className="bg-gray-600 px-2 py-1 rounded text-xs">
                            {transaction.coin}
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <StatusBadge
                          status={transaction.status}
                          refunded={transaction.redbillerStatus === 'refunded'}
                        />
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-white">
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </div>
                        <div className="text-gray-400 text-xs">
                          {new Date(transaction.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {data?.getUserTransactions && (
            <div className="flex items-center justify-between px-6 py-4 bg-gray-800">
              <div className="text-sm text-gray-400">
                Showing {data.getUserTransactions.data.length} of {data.getUserTransactions.total} transactions
                (Page {data.getUserTransactions.page} of {data.getUserTransactions.totalPages})
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white transition-colors"
                >
                  <FiChevronLeft />
                </button>
                <span className="px-3 py-1 text-sm text-gray-300">
                  Page {currentPage} of {data.getUserTransactions.totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage >= data.getUserTransactions.totalPages}
                  className="p-2 rounded-lg bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white transition-colors"
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};